/**
 * postMessage service for iframe <-> parent window communication
 */

import {
  PostMessageEvent,
  PostMessageEventType,
  CardFormReadyPayload,
  CardFormSuccessPayload,
  CardFormErrorPayload,
  CardFormErrorCode,
  CardFormResizePayload,
  isPostMessageEvent,
} from '../types/PostMessageEvent';

export type MessageHandler<T = unknown> = (payload: T, event: MessageEvent) => void;

export class PostMessageService {
  private handlers: Map<PostMessageEventType, Set<MessageHandler>>;
  private allowedOrigins: string[];
  private targetWindow: Window | null = null;
  private messageQueue: PostMessageEvent[] = [];
  private isReady: boolean = false;
  private detectedParentOrigin: string | null = null;

  constructor(allowedOrigins: string[] = []) {
    this.handlers = new Map();
    this.allowedOrigins = allowedOrigins;

    // Bind event listener
    this.handleMessage = this.handleMessage.bind(this);
    window.addEventListener('message', this.handleMessage);

    // Set target window to parent if in iframe
    if (window.parent !== window) {
      this.targetWindow = window.parent;

      // Try to detect parent origin from document.referrer
      if (document.referrer) {
        try {
          const referrerUrl = new URL(document.referrer);
          const referrerOrigin = referrerUrl.origin;

          // Validate against allowed origins
          if (this.allowedOrigins.length === 0 || this.allowedOrigins.includes(referrerOrigin)) {
            this.detectedParentOrigin = referrerOrigin;
            console.log('[PostMessage] Detected parent origin from referrer:', referrerOrigin);
          }
        } catch (error) {
          console.warn('[PostMessage] Could not parse referrer:', error);
        }
      }
    }
  }

  /**
   * Registers a message handler for a specific event type
   * @param type - Event type to listen for
   * @param handler - Handler function
   */
  on<T = unknown>(type: PostMessageEventType, handler: MessageHandler<T>): void {
    if (!this.handlers.has(type)) {
      this.handlers.set(type, new Set());
    }
    this.handlers.get(type)!.add(handler as MessageHandler);
  }

  /**
   * Unregisters a message handler
   * @param type - Event type
   * @param handler - Handler function to remove
   */
  off<T = unknown>(type: PostMessageEventType, handler: MessageHandler<T>): void {
    const handlers = this.handlers.get(type);
    if (handlers) {
      handlers.delete(handler as MessageHandler);
    }
  }

  /**
   * Sends a message to the target window
   * @param type - Event type
   * @param payload - Message payload
   * @param correlationId - Optional correlation ID
   */
  send<T = unknown>(
    type: PostMessageEventType,
    payload: T,
    correlationId?: string
  ): void {
    const message: PostMessageEvent<T> = {
      type,
      payload,
      correlationId,
      timestamp: new Date().toISOString(),
    };

    if (!this.targetWindow) {
      console.warn('[PostMessage] Target window not set, queuing message:', type);
      this.messageQueue.push(message);
      return;
    }

    // Determine target origin
    // Priority: 1) Detected parent origin, 2) First allowed origin, 3) Wildcard
    let targetOrigin: string = '*';
    if (this.detectedParentOrigin) {
      targetOrigin = this.detectedParentOrigin;
    } else if (this.allowedOrigins.length > 0 && this.allowedOrigins[0]) {
      targetOrigin = this.allowedOrigins[0];
    }

    try {
      this.targetWindow.postMessage(message, targetOrigin);
      // SECURITY: Do not log payload as it may contain sensitive information
      console.debug('[PostMessage] Sent message type:', type, 'to origin:', targetOrigin);
    } catch (error) {
      console.error('[PostMessage] Send error for type:', type, 'error:', error);
    }
  }

  /**
   * Sends a CARD_FORM_READY message
   * @param version - Widget version
   */
  sendReady(version: string = '1.0.0'): void {
    const payload: CardFormReadyPayload = {
      version,
      ready: true,
    };
    this.send('CARD_FORM_READY', payload);
    this.isReady = true;

    // Flush queued messages
    this.flushMessageQueue();
  }

  /**
   * Sends a CARD_FORM_SUCCESS message
   * @param data - Success payload
   */
  sendSuccess(data: CardFormSuccessPayload): void {
    this.send('CARD_FORM_SUCCESS', data);
  }

  /**
   * Sends a CARD_FORM_ERROR message
   * @param error - Error message
   * @param errorCode - Optional error code
   * @param details - Optional error details
   */
  sendError(error: string, errorCode?: CardFormErrorCode, validationErrors?: string[]): void {
    const payload: CardFormErrorPayload = {
      error,
      errorCode,
      validationErrors,
    };
    this.send('CARD_FORM_ERROR', payload);
  }

  /**
   * Sends a CARD_FORM_RESIZE message
   * @param height - New height in pixels
   */
  sendResize(height: number): void {
    const payload: CardFormResizePayload = {
      height,
    };
    this.send('CARD_FORM_RESIZE', payload);
  }

  /**
   * Sends a CARD_FORM_CANCEL message
   */
  sendCancel(): void {
    this.send('CARD_FORM_CANCEL', {});
  }

  /**
   * Handles incoming postMessage events
   * @param event - MessageEvent from window
   */
  private handleMessage(event: MessageEvent): void {
    // SECURITY: Do not log event.data as it may contain sensitive information (card data, encrypted payloads)
    console.log('[PostMessage] Message received from origin:', event.origin);

    // Validate origin if allowed origins are specified
    if (this.allowedOrigins.length > 0 && !this.allowedOrigins.includes(event.origin)) {
      console.warn('[PostMessage] REJECTED: Message from unauthorized origin:', event.origin, 'allowed:', this.allowedOrigins);
      return;
    }

    // Store detected parent origin from first valid message
    if (!this.detectedParentOrigin && event.origin) {
      this.detectedParentOrigin = event.origin;
      console.log('[PostMessage] Detected parent origin from message:', event.origin);
    }

    // Validate message structure
    if (!isPostMessageEvent(event)) {
      // SECURITY: Do not log event.data - only log the type of the data received
      console.warn('[PostMessage] REJECTED: Invalid message format. Type:', typeof event.data);
      return;
    }

    const message = event.data as PostMessageEvent;
    // SECURITY: Do not log message.payload as it may contain sensitive information
    console.debug('[PostMessage] Received message type:', message.type);

    // Call registered handlers
    const handlers = this.handlers.get(message.type);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(message.payload, event);
        } catch (error) {
          console.error('[PostMessage] Handler error:', error);
        }
      });
    }
  }

  /**
   * Flushes queued messages after ready
   */
  private flushMessageQueue(): void {
    if (this.messageQueue.length > 0) {
      console.debug('[PostMessage] Flushing queued messages:', this.messageQueue.length);
      this.messageQueue.forEach((message) => {
        this.send(message.type, message.payload, message.correlationId);
      });
      this.messageQueue = [];
    }
  }

  /**
   * Sets allowed origins for security
   * @param origins - Array of allowed origins
   */
  setAllowedOrigins(origins: string[]): void {
    this.allowedOrigins = origins;
  }

  /**
   * Sets the target window for messages
   * @param window - Target window (usually parent)
   */
  setTargetWindow(window: Window): void {
    this.targetWindow = window;
  }

  /**
   * Cleans up event listeners
   */
  destroy(): void {
    window.removeEventListener('message', this.handleMessage);
    this.handlers.clear();
    this.messageQueue = [];
  }

  /**
   * Gets ready status
   */
  getIsReady(): boolean {
    return this.isReady;
  }
}

// Export singleton instance
export const postMessageService = new PostMessageService();

/**
 * Creates a new PostMessageService instance
 * @param allowedOrigins - Array of allowed origins
 * @returns PostMessageService instance
 */
export function createPostMessageService(allowedOrigins: string[] = []): PostMessageService {
  return new PostMessageService(allowedOrigins);
}
